import { AddressAutofill } from "types/Models/addressAutofill";
import { Combobox, OptionData } from "./DesignSystem/Combobox/Combobox";
import { DirectorAutofill } from "types/Models/directorAutofill";
import { BankAutofill } from "types/Models/bankAutofill";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ComponentProps, useState } from "react";
import {
  useCreateAutofillMutation,
  useGetAutofillQuery,
  useUpdateAutofillMutation,
} from "store/apis/autofill";
import { FormikValues } from "formik";
import { Button } from "./DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import { PersonaModal, PersonaModalProps } from "./AutoFill/PersonaModal";
import { AddFilingManualBank } from "components/fileTax/AddFilingManualBank";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { MultiValue, SingleValue } from "react-select";
import { AddressComponents } from "./AddressComponent/AddressComponent";
import { EditPencil } from "components/icons/EditPencil";

type SelectAddressAutofillProps = {
  type: "authorized_persons" | "addresses" | "bank_accounts";
  selected?: string;
  onSelect?: (
    autofill:
      | BankAutofill
      | AddressAutofill
      | PersonaAutofill
      | DirectorAutofill
  ) => void;
  entityId: string;
  groupId?: string;
  name: string;
  withForm?: boolean;
  label: string;
  placeholder?: string;
  canAddMore?: boolean;
  required?: boolean;
  onChange?: (uuid: string) => void;
  menuPortal?: HTMLElement | null;
  hideClearAction?: boolean;
  menuPlacement?: ComponentProps<typeof Combobox>["menuPlacement"];
  srollToSave?: boolean;
  allowedCountries?: string[];
  customValidation?: {
    authorized_persons?: any;
    addresses?: any;
    bank_accounts?: any;
  };
  isDisabled?: boolean;
  renderPersonaModal?: (props: PersonaModalProps) => React.JSX.Element;
  size?: "small" | "regular";
  error?: string;
};

const defaultRenderPersonaModal = (props: PersonaModalProps) => (
  <PersonaModal {...props} />
);

const personInitials = (firstName?: string, lastName?: string) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

const buildPersonOptionLabel = (autofill: PersonaAutofill) => (
  <div className="t-flex t-min-w-0 t-flex-1 t-items-center t-gap-3">
    <span className="t-flex t-h-8 t-w-8 t-flex-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-neutral-0 t-text-body-sm t-font-medium t-text-neutral-40">
      {personInitials(autofill.first_name, autofill.last_name)}
    </span>
    <div className="t-flex t-min-w-0 t-flex-col">
      <span className="t-truncate t-text-body t-text-text-60">
        {autofill.first_name} {autofill.last_name}
      </span>
      {autofill.email && (
        <span className="t-truncate t-text-body-sm t-font-light t-text-text-30">
          {autofill.email}
        </span>
      )}
    </div>
  </div>
);

const buildPersonSelectedValueLabel = (autofill: PersonaAutofill) => (
  <span className="t-truncate t-text-body t-text-text-60">
    {autofill.first_name} {autofill.last_name}
  </span>
);

// Plain-string label so react-select's default filterOption/getOptionLabel
// (which stringify option.label) can actually search on it. formatOptionLabel
// below is what's rendered on screen — this string is search-only.
const buildPersonSearchLabel = (autofill: PersonaAutofill) =>
  `${autofill.first_name} ${autofill.last_name}${
    autofill.email ? ` ${autofill.email}` : ""
  }`.trim();

export const SelectAutofillCombobox = ({
  type,
  selected,
  entityId,
  groupId: groupIdFromProp,
  onSelect,
  canAddMore = true,
  onChange,
  menuPortal = document.body,
  hideClearAction = false,
  srollToSave,
  allowedCountries,
  customValidation,
  renderPersonaModal,
  ...props
}: SelectAddressAutofillProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const finalGroupId = groupId || groupIdFromProp;
  const { data: autofills = [], isLoading } = useGetAutofillQuery(
    {
      groupId: finalGroupId!,
      autofillKey: type,
      entityId,
    },
    { skip: !finalGroupId || !entityId }
  );

  const addModal = useModal();
  const { successToast, alertToast } = useToast();
  const [personaInitialState, setPersonaInitialState] =
    useState<PersonaAutofill>();

  const [createAutofill, { isLoading: addingAutofill }] =
    useCreateAutofillMutation();
  const [editAutofill, { isLoading: isUpdatingPersona }] =
    useUpdateAutofillMutation();

  const onAddAutofill = async (values: FormikValues) => {
    try {
      const autofill = await createAutofill({
        fields: values,
        groupId: finalGroupId!,
        autofillKey: type,
        entityId,
      }).unwrap();

      onSelect?.(autofill);
      addModal.close();
      successToast({ message: "Autofill added successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const currentAutofill = autofills.find((a) => a.uuid === selected);

  let comboboxComponent = {};
  if (hideClearAction) {
    comboboxComponent = { ClearIndicator: () => null };
  }

  if (type === "addresses") {
    return (
      <AddressComponents
        formPrefix=""
        entityId={entityId}
        groupId={finalGroupId!}
        required={props.required}
        allowedCountries={allowedCountries}
        selectedAddressId={currentAutofill?.uuid}
        label={props.label || "Address"}
        size={props.size}
        onAddressSelect={(address) => {
          onChange?.(address?.uuid || "");
          if (address) {
            onSelect?.(address);
          }
        }}
        hideClear
        srollToSave={srollToSave}
        isDisabled={props.isDisabled}
        error={props.error}
      />
    );
  }

  const handleOnEdit = () => {
    if (currentAutofill) {
      setPersonaInitialState({
        ...currentAutofill,
        id: currentAutofill?.uuid,
      });
      addModal.open();
    }
  };

  const editPersona = async (values: FormikValues) => {
    try {
      (await editAutofill({
        groupId: finalGroupId!,
        autofillKey: type,
        fields: values,
        uuid: values.id,
        entityId,
      }).unwrap()) as PersonaAutofill;
      successToast({ message: "Autofill updated successfully!" });
      addModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <div>
      <Combobox
        {...props}
        isLoading={isLoading}
        components={comboboxComponent}
        options={autofills?.map((autofill) => ({
          value: autofill.uuid,
          label:
            type === "authorized_persons"
              ? buildPersonSearchLabel(autofill)
              : autofill.autofill_string,
        }))}
        allowEdit={type === "authorized_persons"}
        handleEdit={handleOnEdit}
        value={
          currentAutofill?.uuid
            ? {
                label:
                  type === "authorized_persons"
                    ? buildPersonSearchLabel(currentAutofill)
                    : currentAutofill.autofill_string,
                value: currentAutofill.uuid,
              }
            : null
        }
        formatOptionLabel={
          type === "authorized_persons"
            ? (option: OptionData, meta: { context: "menu" | "value" }) => {
                const autofill = autofills.find((a) => a.uuid === option.value);
                if (!autofill) return option.label;
                if (meta.context === "value") {
                  return buildPersonSelectedValueLabel(autofill);
                }
                const isSelectedOption =
                  autofill.uuid === currentAutofill?.uuid;
                return (
                  <div className="t-flex t-w-full t-items-center t-justify-between t-gap-2">
                    {buildPersonOptionLabel(autofill)}
                    {isSelectedOption && (
                      <Button
                        customType="ghost_icon"
                        className="t-text-text-30 hover:t-text-text-60"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOnEdit();
                        }}
                      >
                        <EditPencil />
                      </Button>
                    )}
                  </div>
                );
              }
            : undefined
        }
        actions={
          canAddMore && (
            <Button
              onClick={() => {
                setPersonaInitialState(undefined);
                addModal.open();
              }}
              customType="link"
            >
              Add {type === "authorized_persons" && "New"}
              {type === "bank_accounts" && "Bank Account"}
            </Button>
          )
        }
        menuPortalTarget={menuPortal || null}
        onChange={(
          selectedOption: MultiValue<OptionData> | SingleValue<OptionData>
        ) => {
          const uuid = (selectedOption as SingleValue<OptionData>)?.value || "";
          onChange?.(uuid);
          const selectedAutofill = autofills.find(
            (autofill) => autofill.uuid === uuid
          );
          if (selectedAutofill) {
            onSelect?.(selectedAutofill);
          }
        }}
      />

      {type === "authorized_persons" &&
        (renderPersonaModal || defaultRenderPersonaModal)({
          entityId,
          open: addModal.isOpen,
          onSubmit: personaInitialState?.id ? editPersona : onAddAutofill,
          onClose: addModal.close,
          isLoading: addingAutofill || isUpdatingPersona,
          initialValues: personaInitialState || {},
          customValidation: customValidation?.authorized_persons,
        })}

      {type === "bank_accounts" && (
        <AddFilingManualBank
          isOpenAdd={addModal.isOpen}
          isOpenEdit={addModal.isOpen}
          onClose={addModal.close}
          entityId={entityId}
          groupId={finalGroupId!}
          autofillKey="bank_accounts"
          onSuccess={(bank) => onSelect?.(bank)}
        />
      )}
    </div>
  );
};
